import { createColumnHelper } from "@tanstack/react-table";
import { faDownload, faEye } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { dateSearchingFn, numberSearchingFn } from "cpl-react-common/components/Tables/TableFilterFunctions";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getSummaries,
  selectLoadingSummaries,
  selectServiceChargeSummaries,
} from "./ServiceChargeInvoicingSummariesSlice";
import { ServiceChargeInvoiceSummaryModel } from "services/api/cplapi/CPLAPI";
import { useEffect, useState } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { formatDate } from "cpl-react-common/Helpers/StringHelpers";
import "./ServiceChargeInvoicingStyles.scss";
import CSVExport from "cpl-react-common/components/Actions/CSVExport";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import { checkUserHasPermission } from "services/userSession/UserPermittedSlice";

const ServiceChargeInvoicingSummaries: React.FC = () => {
  const thunkDispatch = useThunkDispatch();
  const navigate = useNavigate();
  const canViewDetails = checkUserHasPermission(
    (permission) => permission === Permissions.ViewServiceChargeInvoiceDetails
  );
  const loadingSummaries = useSelector(selectLoadingSummaries);
  const summaries = useSelector(selectServiceChargeSummaries);
  const [filteredSummaries, setFilteredSummaries] = useState(summaries);

  const [completedFromDate, setCompletedFromDate] = useState<Date | undefined>();
  const [completedToDate, setCompletedToDate] = useState<Date | undefined>();

  const columnHelper = createColumnHelper<ServiceChargeInvoiceSummaryModel>();

  useEffect(() => {
    const filteringActive = completedFromDate != undefined || completedToDate != undefined;

    if (filteringActive) {
      setFilteredSummaries(
        summaries?.filter((s) => {
          if (s.completedDate == undefined) return false;

          const completedDate = new Date(
            Date.UTC(s.completedDate!.getUTCFullYear(), s.completedDate!.getUTCMonth(), s.completedDate!.getUTCDate())
          );

          if (completedToDate && completedDate.getTime() > completedToDate.getTime()) return false;
          if (completedFromDate && completedDate.getTime() < completedFromDate.getTime()) return false;

          return true;
        })
      );
    } else {
      setFilteredSummaries(summaries);
    }
  }, [completedFromDate, completedToDate, summaries]);

  useEffect(() => {
    thunkDispatch(getSummaries());
  }, [thunkDispatch]);

  const columnDefinition = [
    columnHelper.accessor((row) => row.requestedDate, {
      id: "Requested",
      cell: (info) => info.getValue()?.toCPLDateTimeString(),
      header: () => <>Requested Event Date/Time</>,
      filterFn: dateSearchingFn,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.requestedDate, {
      id: "Requested",
      cell: (info) => (
        <>
          <div className="text-muted">Requested</div>
          {info.getValue()?.toCPLDateTimeString()}
        </>
      ),
      header: () => <>Requested Event Date/Time</>,
      filterFn: dateSearchingFn,
      meta: {
        columnClassName: "showTableCellOnMobile",
      },
    }),
    columnHelper.accessor((row) => row.completedDate, {
      id: "Completed",
      cell: (info) => info.getValue()?.toCPLDateTimeString() ?? "-",
      header: () => <>Completed Event Date/Time</>,
      filterFn: dateSearchingFn,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.completedDate, {
      id: "Completed",
      cell: (info) => (
        <>
          <div className="text-muted">Completed</div>
          {info.getValue()?.toCPLDateTimeString() ?? "-"}
        </>
      ),
      header: () => <>Completed Event Date/Time</>,
      filterFn: dateSearchingFn,
      meta: {
        columnClassName: "showTableCellOnMobile",
      },
    }),
    columnHelper.accessor((row) => row.batchNumber, {
      id: "Batch Number",
      cell: (info) => info.getValue()?.toString(),
      header: () => <>Batch Number</>,
      filterFn: numberSearchingFn,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.status, {
      id: "Status",
      cell: (info) => info.getValue(),
      header: () => <>Status</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
    columnHelper.accessor((row) => row.status, {
      id: "Status",
      cell: (info) => (
        <>
          <div className="text-muted">Status</div>
          {info.getValue()}
        </>
      ),
      header: () => <>Status</>,
      meta: {
        columnClassName: "showTableCellOnMobile",
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <Permission permissionPredicate={(permission) => permission === Permissions.ViewServiceChargeInvoiceDetails}>
          <div
            data-test-id={`service-charge-invoice-summaries-page__view-details-button-${info.row.original.logID}`}
            className="btn btn-icon btn-primary"
            onClick={() => {
              redirectToDetails(info.row.original);
            }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="View details"
          >
            <FontAwesomeIcon icon={faEye} />
          </div>
        </Permission>
      ),
      enableHiding: false,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
    }),
  ];

  const redirectToDetails = (detailsRow: ServiceChargeInvoiceSummaryModel) => {
    if (!canViewDetails) {
      return;
    }

    const path = `/ServiceChargeInvoice/${detailsRow.logID}`;
    navigate(path);
  };

  return (
    <div className="card d-inline-flex w-100`">
      <div className="card-header">
        <div className="card-title">Service Charge Invoice Summaries</div>
        <div className="card-toolbar">
          <Permission permissionPredicate={(permission) => permission === Permissions.GenerateServiceChargeInvoice}>
            <Link
              data-test-id="service-charge-invoice-summaries-page__generate-invoices-link"
              className="btn btn-primary ms-1"
              to={`/ServiceChargeInvoicing/Wizard`}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Generate Service Charge Invoices"
            >
              Generate Invoices
            </Link>
          </Permission>
        </div>
      </div>
      <div className="card-body">
        <ActivityIndicator isBusy={loadingSummaries}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-6 align-items-center mb-4">
              <div className="d-flex flex-column">
                <label htmlFor="completedFrom">Completed From:</label>
                <input
                  data-test-id="service-charge-invoice-summaries-page__completed-from-date-input"
                  id="completedFrom"
                  type="date"
                  className="form-control form-control-solid completed-date-pickers"
                  defaultValue={completedFromDate ? formatDate(completedFromDate) : undefined}
                  onChange={(event) => {
                    const date = new Date(event.target.valueAsNumber);
                    setCompletedFromDate(date.isValidDate() ? date : undefined);
                  }}
                />
              </div>
              <div className="d-flex flex-column">
                <label htmlFor="completedTo">Completed To:</label>
                <input
                  data-test-id="service-charge-invoice-summaries-page__completed-to-date-input"
                  id="completedTo"
                  type="date"
                  className="form-control form-control-solid completed-date-pickers"
                  defaultValue={completedToDate ? formatDate(completedToDate) : undefined}
                  onChange={(event) => {
                    const date = new Date(event.target.valueAsNumber);
                    setCompletedToDate(date.isValidDate() ? date : undefined);
                  }}
                ></input>
              </div>
            </div>
            {filteredSummaries && (
              <div>
                <CSVExport
                  data-test-id="service-charge-invoice-summaries-page__export-csv-button"
                  headers={Object.keys(new ServiceChargeInvoiceSummaryModel())}
                  data={filteredSummaries}
                  fileName={`Service Charge Summaries ${new Date().toCPLDateTimeString()}`}
                  applyStyles={false}
                >
                  <div className="fs-4">
                    <span className="me-2">Export</span>
                    <FontAwesomeIcon icon={faDownload as IconProp} />
                  </div>
                </CSVExport>
              </div>
            )}
          </div>
          <CPLTable
            data={filteredSummaries}
            columns={columnDefinition}
            key={loadingSummaries.toString()}
            sortingState={[{ id: "Completed", desc: true }]}
            pagination={true}
            rowMobileClickAction={(row) => redirectToDetails(row)}
          />
        </ActivityIndicator>
      </div>
    </div>
  );
};

export default ServiceChargeInvoicingSummaries;
